/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="../adonisrc.ts" />
/// <reference path="../config/inertia.ts" />
/// <reference path="../config/auth.ts" />
/// <reference path="../config/bodyparser.ts" />
/// <reference path="../config/drive.ts" />
/// <reference path="../config/ally.ts" />

import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './css/app.css'
import { resolvePageComponent } from '@adonisjs/inertia/helpers'
import { TuyauProvider } from '@adonisjs/inertia/react'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import * as Sentry from '@sentry/react'
import { type BrowserOptions } from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { hydrateRoot } from 'react-dom/client'
import { ThemeProvider } from '~/context/ThemeContext'
import { queryClient, tuyau } from '~/core/tuyau'

// Sentry configuration
const sentryConfig: BrowserOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  tunnel: '/api/v1/monitoring',
  environment: import.meta.env.VITE_APP_ENV || 'production',
  // Restrict trace header propagation (baggage, sentry-trace) to same-origin
  // requests only. Without this, Sentry would inject these headers on every
  // outgoing XHR/fetch, including cross-origin calls to the Keycloak server,
  // which rejects the unknown `baggage` header and breaks logout.
  tracePropagationTargets: [/^\//, import.meta.env.VITE_APP_URL].filter(Boolean),
  integrations: [
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: false,
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn'],
    }),
  ],
  tracesSampleRate: import.meta.env.VITE_APP_ENV === 'development' ? 1.0 : 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    // Filter out chunk load errors: a stale tab (open across a deploy) requests an
    // asset hash Vite no longer serves. Not actionable — the fix is reloading, which
    // we don't force since it would drop in-progress form state.
    // Webpack throws a typed ChunkLoadError; Vite throws a plain TypeError whose
    // message says "error loading"/"failed to fetch ... dynamically imported module"
    // (Sentry NODE-1Y — 9 events over 7 months, the existing `type` check never
    // matched Vite's shape).
    if (event.exception) {
      const error = event.exception.values?.[0]
      const isChunkLoadError =
        error?.type === 'ChunkLoadError' || /dynamically imported module/i.test(error?.value ?? '')
      if (isChunkLoadError) {
        return null
      }

      // Filter errors thrown from inside the clarity-js library's own async internals
      // (an unhandled rejection unrelated to any call we make into it — wrapping our
      // own identify()/event()/set() calls wouldn't catch it). Match by file path, not
      // message: the message wording differs per browser ("Cannot read properties of
      // null (reading 'sequence')" in Chrome, "can't access property "sequence", J is
      // null" in Firefox) for what is the same upstream bug (Sentry NODE-2E / NODE-47).
      const isClarityInternalError = error?.stacktrace?.frames?.some((frame) =>
        frame.filename?.includes('clarity')
      )
      if (isClarityInternalError) {
        return null
      }
    }
    return event
  },
}

createInertiaApp({
  progress: { color: '#5468FF' },

  title: (title) => title,

  resolve: (name) => {
    return resolvePageComponent(
      `./pages/${name}.tsx`,
      import.meta.glob('./pages/**/*.tsx')
    ) as Promise<ResolvedComponent>
  },

  setup({ el, App, props }) {
    Sentry.init(sentryConfig)
    hydrateRoot(
      el,
      <ThemeProvider defaultTheme="system" storageKey="sota-ui-theme">
        <TuyauProvider client={tuyau}>
          <QueryClientProvider client={queryClient}>
            <App {...props} />
          </QueryClientProvider>
        </TuyauProvider>
      </ThemeProvider>
    )
  },
})
